/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState, useCallback } from 'react';
import throttle from 'lodash/throttle';

import smoothScrolling from 'Modules/smoothScrolling';

import { displayDebugInfo, highliteElement } from './debug';

interface Options {
  topScrollLimit?: number;
  onEnterView?: (elementId: string) => void;
  selectedId?: string | null;
  debug?: boolean;
  isReady?: boolean;
}

/*
  During scrolling, imagine a line positioned to `topScrollLimit` px from top,
  all elements matching `elementsSelectors` will be check on scroll, and if they cross the line,
  `onChange` is called.
 */
export default function useElementInViewPort(
  elementsSelectors: string,
  {
    topScrollLimit = 180,
    onEnterView = () => {},
    selectedId = null,
    debug = false,
    isReady = true,
  }: Options = {},
  dependencies = []
) {
  const [elements, setElements] = useState<Element[] | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const scrollToElement = (element: Element, speed?: number) => {
    setIsScrolling(true);
    smoothScrolling.scrollToElement(element, {
      offset: 80,
      speed,
      onComplete() {
        setIsScrolling(false);
      },
    });
  };

  const isActiveElement = useCallback(
    (element: Element) => {
      const { top, height } = element.getBoundingClientRect();
      return top < topScrollLimit && top + height > topScrollLimit;
    },
    [topScrollLimit]
  );

  const update = useCallback(() => {
    elements?.forEach((element) => {
      const isNotCurrentSelectedId = selectedId !== element.id;

      if (isNotCurrentSelectedId && isActiveElement(element)) {
        onEnterView(element.id);
        window.history.pushState(null, '', `#${element.id}`);

        if (debug) {
          highliteElement(elements, element);
        }
      }
    });
  }, [elements, selectedId, isActiveElement, onEnterView, debug]);

  // initialize, if a hash is present in the url scroll to that element
  useEffect(() => {
    if (!elements) {
      return;
    }

    const urlHash = document.location.hash.replace('#', '');
    if (!urlHash) {
      return;
    }

    const element = document.getElementById(urlHash);

    if (!element) {
      return;
    }

    scrollToElement(element, 0);
  }, [elements]);

  // get all elements we want to monitor
  useEffect(() => {
    if (isReady) {
      setElements([...document.querySelectorAll(elementsSelectors)]);
    }
  }, [elementsSelectors, isReady, ...dependencies]);

  // scroll to correct element when selectedId changes
  useEffect(() => {
    if (!selectedId || !isReady) {
      return;
    }

    const targetElement = document.getElementById(selectedId);
    if (!targetElement || isActiveElement(targetElement)) {
      return;
    }

    scrollToElement(targetElement);

    if (debug) {
      highliteElement(elements, targetElement);
    }
  }, [debug, elements, isActiveElement, selectedId, isReady, ...dependencies]);

  // add scroll handler
  useEffect(() => {
    if (!elements) {
      return undefined;
    }

    const scrollHandler = throttle(() => {
      if (isScrolling) {
        return;
      }

      update();
    }, 100);

    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [update, elementsSelectors, topScrollLimit, elements, isScrolling]);

  // display debug information if needed
  useEffect(() => {
    if (debug) {
      displayDebugInfo(elements, topScrollLimit, selectedId);
    }
  }, [debug, elements, selectedId, topScrollLimit]);

  return { selectedId, update };
}
